import { Component, forwardRef, inject, input, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { StrapiFile } from '@common/core/models';
import { BitfFile } from '@common/libs/bitforce/core/models';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'casa-doc-circular-photo-upload',
  standalone: false,
  templateUrl: './circular-photo-upload.component.html',
  styleUrl: './circular-photo-upload.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CircularPhotoUploadComponent),
      multi: true,
    },
  ],
})
export class CircularPhotoUploadComponent implements ControlValueAccessor {
  acceptedFormats = input<string>('image/jpeg,image/jpg,image/png');
  maxSizeMB = input<number>(5);

  previewUrl = signal<string | null>(null);
  fileName = signal<string | null>(null);
  errorMessage = signal<string | null>(null);
  disabled = signal<boolean>(false);

  private translateService = inject(TranslateService);

  private onChange: (value: BitfFile | StrapiFile | null) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: BitfFile | StrapiFile | null): void {
    if (value) {
      if (value instanceof StrapiFile) {
        this.previewUrl.set(value.url);
        this.fileName.set(value.name);
      } else if (value instanceof BitfFile) {
        const reader = new FileReader();
        reader.onload = (e: ProgressEvent<FileReader>) => {
          this.previewUrl.set(e.target?.result as string);
        };
        reader.readAsDataURL(value.fileObject);
        this.fileName.set(value.fileObject.name);
      }
    } else {
      this.previewUrl.set(null);
      this.fileName.set(null);
    }
  }

  registerOnChange(fn: (value: BitfFile | StrapiFile | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  onFileSelected(event: Event): void {
    if (this.disabled()) {
      return;
    }
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.handleFile(file);
    }
  }

  onDragOver(event: DragEvent): void {
    if (this.disabled()) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent): void {
    if (this.disabled()) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];
      this.handleFile(file);
    }
  }

  private handleFile(file: File): void {
    this.errorMessage.set(null);

    // Validate file size
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > this.maxSizeMB()) {
      this.errorMessage.set(this.translateService.instant('BITF.FILE_UPLOADER.VALIDATION_FILE_SIZE_ERROR'));
      return;
    }

    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      this.previewUrl.set(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    this.fileName.set(file.name);

    const bitfFile = new BitfFile(file);
    this.onChange(bitfFile);
    this.onTouched();
  }

  removePhoto(): void {
    this.previewUrl.set(null);
    this.fileName.set(null);
    this.errorMessage.set(null);

    this.onChange(null);
    this.onTouched();
  }

  triggerFileInput(): void {
    if (this.disabled()) {
      return;
    }
    const fileInput = document.getElementById('photo-file-input') as HTMLInputElement;
    fileInput?.click();
  }
}
